import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { hieraApi, type Me } from "./lib/api";
import type { Lang } from "./lib/i18n";
import { AppShell } from "./shell/AppShell";
import { HomePage } from "./pages/HomePage";
import { SpacePage } from "./pages/SpacePage";
import { DocumentPage } from "./pages/DocumentPage";
import { SharePage } from "./pages/SharePage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SettingsPage } from "./pages/SettingsPage";
import { PeoplePage } from "./pages/PeoplePage";
import { HeronCallback } from "./pages/HeronCallback";
import { canAdminRole } from "./lib/roles";
import { readTheme, type ThemeId } from "./lib/theme";
import { destroyHerald, syncHerald } from "./lib/herald";
import "./styles.css";

type Theme = ThemeId;

function AuthedRoutes({
  me,
  lang,
  setLang,
  theme,
  setTheme,
  onMe,
  onChanged,
}: {
  me: Me;
  lang: Lang;
  setLang: (lang: Lang) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  onMe: (me: Me) => void;
  onChanged: () => void;
}) {
  return (
    <Routes>
      <Route path="/" element={<HomePage lang={lang} me={me} onChanged={onChanged} />} />
      <Route path="/s/:spaceId" element={<SpacePage lang={lang} />} />
      <Route path="/p/:pageId" element={<DocumentPage lang={lang} />} />
      <Route path="/templates" element={<TemplatesPage lang={lang} />} />
      <Route path="/me" element={<ProfilePage lang={lang} me={me} onMe={onMe} />} />
      <Route
        path="/settings"
        element={
          <SettingsPage
            lang={lang}
            setLang={setLang}
            theme={theme}
            setTheme={setTheme}
            staff={canAdminRole(me.globalRole)}
          />
        }
      />
      <Route path="/people" element={<PeoplePage lang={lang} />} />
      <Route
        path="/admin"
        element={canAdminRole(me.globalRole) ? <PeoplePage lang={lang} staff /> : <Navigate to="/" replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [lang, setLang] = useState<Lang>((localStorage.getItem("hiera.lang") as Lang) || "ru");
  const [spaces, setSpaces] = useState<Array<{ id: string; title: string; slug: string }>>([]);
  const [booted, setBooted] = useState(false);
  const [theme, setTheme] = useState<Theme>(() => {
    const next = readTheme();
    document.documentElement.dataset.theme = next;
    return next;
  });

  const refresh = useCallback(async () => {
    try {
      const user = await hieraApi.me();
      setMe(user);
      const home = await hieraApi.home();
      setSpaces(home.spaces.map((s) => ({ id: s.id, title: s.title, slug: s.slug })));
    } catch {
      setMe(null);
      setSpaces([]);
    } finally {
      setBooted(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    localStorage.setItem("hiera.lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("hiera.theme", theme);
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  if (!booted) return <div className="boot">Hiera…</div>;

  return (
    <BrowserRouter>
      <HeraldGate me={me} lang={lang} />
      <Routes>
        <Route path="/share/:token" element={<SharePage lang={lang} />} />
        <Route path="/auth/heron-callback" element={<HeronCallback onDone={refresh} />} />
        <Route path="/auth/heron-callback/" element={<HeronCallback onDone={refresh} />} />
        <Route
          path="/*"
          element={
            <AppShell
              me={me}
              lang={lang}
              setLang={setLang}
              spaces={spaces}
              theme={theme}
              setTheme={setTheme}
              onAuthed={() => void refresh()}
            >
              {me ? (
                <AuthedRoutes me={me} lang={lang} setLang={setLang} theme={theme} setTheme={setTheme} onMe={setMe} onChanged={() => void refresh()} />
              ) : null}
            </AppShell>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

/** Herald only for authed app chrome — never on public /share links. */
function HeraldGate({ me, lang }: { me: Me | null; lang: Lang }) {
  const loc = useLocation();
  const shareGuest = loc.pathname.startsWith("/share/");

  useEffect(() => {
    if (!me || shareGuest) {
      destroyHerald();
      return;
    }
    void syncHerald(lang);
  }, [me, lang, shareGuest]);

  return null;
}
